import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { Button, Chip } from 'tio-ui/components/buttons';
import { and } from 'tio-ui/utilities';
import { PhReceipt } from 'ember-phosphor-icons';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import formatCurrencySuperscriptCents from 'tio-common/helpers/format-currency-superscript-cents';
import type { CourseCashTransaction } from 'tio-common/types/course-cash-transaction';
import { formatTransactionDate } from 'tio-common/utils/course-cash/format-transaction';

interface CardTransactionRowSignature {
  Args: {
    transaction: CourseCashTransaction;
    onUploadReceipt?: (transaction: CourseCashTransaction) => void;
  };
  Element: HTMLDivElement;
}

export default class CardTransactionRow extends Component<CardTransactionRowSignature> {
  @service declare intl: IntlService;

  get formattedDate() {
    return formatTransactionDate(this.args.transaction.transactionDate, this.intl);
  }

  get formattedAmount() {
    return this.args.transaction.amount;
  }

  get needsReceipt() {
    return this.args.transaction.receiptStatus === 'needed';
  }

  get isInReview() {
    return this.args.transaction.receiptStatus === 'uploaded';
  }

  get isVerified() {
    return this.args.transaction.receiptStatus === 'verified';
  }

  get isRejected() {
    return this.args.transaction.receiptStatus === 'rejected';
  }

  get hasSubmittedReceipt() {
    return this.isInReview || this.isVerified || this.isRejected;
  }

  get receiptCount() {
    return this.args.transaction.receipts?.length ?? 0;
  }

  get receiptDisplayText() {
    if (this.receiptCount === 1) {
      return this.intl.t('tas.course_cash.receipt_uploaded');
    }

    if (this.receiptCount > 1) {
      return this.intl.t('tas.course_cash.receipts_uploaded', {
        count: this.receiptCount,
      });
    }

    return '';
  }

  handleUploadReceipt = () => {
    this.args.onUploadReceipt?.(this.args.transaction);
  };

  <template>
    <div
      class="flex items-center gap-6 py-4 border-b border-gray-200 last:border-b-0"
      data-test-card-transaction-row
      ...attributes
    >
      {{! Column 1: Icon with status indicator }}
      <div class="relative shrink-0 p-1 w-8 h-8 flex items-center rounded-md">
        <PhReceipt @size={{24}} @weight="regular" />
        {{#if this.needsReceipt}}
          {{! Red dot for needed status - matches modal styling }}
          <div class="absolute bg-white flex items-center p-0.5 rounded-xl right-0 top-0">
            <div class="bg-red-600 rounded w-2 h-2" data-test-needs-receipt-indicator></div>
          </div>
        {{/if}}
      </div>

      {{! Column 2: Transaction details + Amount - uses flex-grow with justify-between }}
      <div class="flex flex-1 items-center justify-between min-w-0">
        {{! Date and Merchant }}
        <div class="flex flex-col min-w-0">
          <span class="text-xs text-gray-500" data-test-transaction-date>
            {{this.formattedDate}}
          </span>
          <span class="text-sm text-gray-900 font-normal truncate" data-test-merchant-name>
            {{@transaction.merchantName}}
          </span>
        </div>

        {{! Amount - positioned via justify-between }}
        <span
          class="text-base font-semibold text-gray-700 whitespace-nowrap tracking-wide"
          data-test-amount
        >
          {{formatCurrencySuperscriptCents this.formattedAmount}}
        </span>
      </div>

      {{! Column 3: Action area - fixed width for alignment }}
      <div class="shrink-0 w-40 flex justify-end">
        {{#if this.needsReceipt}}
          {{#if @onUploadReceipt}}
            <Button
              @appearance="outlined"
              @intent="primary"
              @size="sm"
              {{on "click" this.handleUploadReceipt}}
              data-test-upload-receipt-button
            >
              {{t "tas.course_cash.upload_receipt"}}
            </Button>
          {{/if}}
        {{else if this.hasSubmittedReceipt}}
          {{! Stacked layout: Chip on top, link below }}
          <div class="flex flex-col items-end gap-1">
            {{#if this.isInReview}}
              <Chip @intent="warning" @appearance="faded" @size="sm" data-test-status-chip>
                {{t "tas.course_cash.in_review"}}
              </Chip>
            {{else if this.isVerified}}
              <Chip @intent="success" @appearance="faded" @size="sm" data-test-status-chip>
                {{t "tas.course_cash.verified"}}
              </Chip>
            {{else if this.isRejected}}
              <Chip @intent="danger" @appearance="faded" @size="sm" data-test-status-chip>
                {{t "tas.course_cash.rejected"}}
              </Chip>
            {{/if}}
            {{#if (and this.receiptCount @onUploadReceipt)}}
              <button
                type="button"
                class="tio-copy text-sm"
                {{on "click" this.handleUploadReceipt}}
                data-test-manage-receipts-button
              >
                {{this.receiptDisplayText}}
              </button>
            {{/if}}
          </div>
        {{/if}}
      </div>
    </div>
  </template>
}
